import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import {
  currentSituationSummary,
  fmtSignedCorr,
  situationAtTime,
} from '../utils/stockBondCorrelationSignal';
import StockBondCorrelationHelp from './StockBondCorrelationHelp';
import './StockBondReturnsPanel.css';

const SCALE_MIN_WIDTH = 72;
const CORR_MIN = -1;
const CORR_MAX = 1;

const EQUITY_SYMBOL = '^GSPC';
const BOND_SYMBOL = 'TLT';
const YIELD_SERIES = 'DGS10';

const RANGE_OPTIONS = [
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '2Y', days: 730 },
  { label: '5Y', days: 1825 },
  { label: 'All', days: null },
];

const CORR_WINDOWS = [
  { label: '30d', days: 30, color: '#A16D17' },
  { label: '60d', days: 60, color: '#3A17A1' },
  { label: '90d', days: 90, color: '#1776A1' },
];

const DEFAULT_RANGE = '1Y';
const COLOR_EQUITY = '#1F4E8C';
const COLOR_BOND = '#1763A1';

const SHARED_CHART_OPTS = {
  layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
  grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
  crosshair: { mode: 1 },
  leftPriceScale: {
    visible: true,
    borderColor: '#E3D9C6',
    minimumWidth: SCALE_MIN_WIDTH,
    scaleMargins: { top: 0.2, bottom: 0.14 },
  },
  rightPriceScale: {
    visible: true,
    borderColor: '#E3D9C6',
    minimumWidth: SCALE_MIN_WIDTH,
    scaleMargins: { top: 0.12, bottom: 0.12 },
  },
  handleScroll: {
    mouseWheel: true,
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: false,
  },
  handleScale: {
    axisPressedMouseMove: true,
    axisDoubleClickReset: true,
    mouseWheel: true,
    pinch: true,
  },
};

async function fetchBars(symbol) {
  const response = await fetch(`/api/investing/prices/${encodeURIComponent(symbol)}`);
  if (!response.ok) {
    throw new Error(`${symbol}: HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.bars || [];
}

async function fetchMacroLatest(seriesId) {
  const response = await fetch(
    `/api/investing/macro/${encodeURIComponent(seriesId)}?days=5`,
  );
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  return data.latest ?? null;
}

function alignCloses(equityBars, bondBars) {
  const bondByTime = new Map(bondBars.map((bar) => [bar.time, bar.close]));
  const aligned = [];

  for (const bar of equityBars) {
    const bondClose = bondByTime.get(bar.time);
    if (bondClose != null) {
      aligned.push({ time: bar.time, equity: bar.close, bond: bondClose });
    }
  }

  return aligned;
}

function toIndexedSeries(aligned) {
  if (!aligned.length) {
    return { equity: [], bond: [], startDate: null };
  }

  const baseEquity = aligned[0].equity;
  const baseBond = aligned[0].bond;

  return {
    startDate: aligned[0].time,
    equity: aligned.map((row) => ({
      time: row.time,
      value: (row.equity / baseEquity) * 100,
    })),
    bond: aligned.map((row) => ({
      time: row.time,
      value: (row.bond / baseBond) * 100,
    })),
  };
}

function dailyReturns(aligned) {
  const returns = [];
  for (let i = 1; i < aligned.length; i += 1) {
    const prev = aligned[i - 1];
    const curr = aligned[i];
    if (prev.equity === 0 || prev.bond === 0) continue;
    returns.push({
      time: curr.time,
      equity: (curr.equity - prev.equity) / prev.equity,
      bond: (curr.bond - prev.bond) / prev.bond,
    });
  }
  return returns;
}

function pearsonCorrelation(xs, ys) {
  const n = xs.length;
  if (n < 2) return null;

  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i += 1) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let num = 0;
  let denX = 0;
  let denY = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }

  const den = Math.sqrt(denX * denY);
  if (den === 0) return null;
  return num / den;
}

function rollingCorrelation(returns, window) {
  const out = [];
  for (let i = window - 1; i < returns.length; i += 1) {
    const slice = returns.slice(i - window + 1, i + 1);
    const corr = pearsonCorrelation(
      slice.map((r) => r.equity),
      slice.map((r) => r.bond),
    );
    if (corr != null && !Number.isNaN(corr)) {
      out.push({ time: returns[i].time, value: corr });
    }
  }
  return out;
}

function visibleRangeForLabel(seriesData, rangeLabel) {
  if (!seriesData.equity.length) return null;

  const to = seriesData.equity[seriesData.equity.length - 1].time;
  const spec = RANGE_OPTIONS.find((r) => r.label === rangeLabel);
  if (!spec || spec.days == null) {
    return { from: seriesData.equity[0].time, to };
  }

  const end = new Date(`${to}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - spec.days);
  const from = start.toISOString().slice(0, 10);
  return { from, to };
}

function applyRange(chart, seriesData, rangeLabel) {
  if (!chart || !seriesData.equity.length) return;

  const range = visibleRangeForLabel(seriesData, rangeLabel);
  if (!range) return;

  try {
    chart.timeScale().setVisibleRange(range);
  } catch {
    try {
      chart.timeScale().fitContent();
    } catch {
      /* empty */
    }
  }
}

function lockCorrelationScale(chart) {
  if (!chart) return;
  try {
    chart.priceScale('right').applyOptions({
      autoScale: false,
      scaleMargins: { top: 0.08, bottom: 0.08 },
    });
    chart.priceScale('right').setVisibleRange({ from: CORR_MIN, to: CORR_MAX });
  } catch {
    /* empty */
  }
}

function fmtIndexed(value) {
  if (value == null || Number.isNaN(value)) return '—';
  const pct = value - 100;
  const sign = pct >= 0 ? '+' : '';
  return `${value.toFixed(1)} (${sign}${pct.toFixed(1)}%)`;
}

function fmtCorr(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(2);
}

function crosshairTimeString(time) {
  if (time == null) return null;
  if (typeof time === 'string') return time.slice(0, 10);
  if (typeof time === 'object' && time.year != null) {
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${time.year}-${m}-${d}`;
  }
  return String(time).slice(0, 10);
}

function formatChartDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${d}, ${y}`;
}

function StockBondReturnsPanel({ fullPage = false }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const chartPayloadRef = useRef(null);
  const seriesRef = useRef({
    equity: null,
    bond: null,
    baseline: null,
    corr30: null,
    corr60: null,
    corr90: null,
    zero: null,
  });
  const rangeRef = useRef(DEFAULT_RANGE);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [seriesData, setSeriesData] = useState({ equity: [], bond: [], startDate: null });
  const [latest, setLatest] = useState({ equity: null, bond: null });
  const [latestYield, setLatestYield] = useState(null);
  const [latestCorr, setLatestCorr] = useState({ d30: null, d60: null, d90: null });
  const [chartPayload, setChartPayload] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);

  const currentSituation = useMemo(
    () => currentSituationSummary(latestCorr.d30, latestCorr.d60, latestCorr.d90),
    [latestCorr],
  );

  useEffect(() => {
    chartPayloadRef.current = chartPayload;
  }, [chartPayload]);

  useEffect(() => {
    rangeRef.current = range;
  }, [range]);

  useEffect(() => {
    if (!chartPayload) return undefined;
    if (!chartContainerRef.current) return undefined;
    if (chartRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      ...SHARED_CHART_OPTS,
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    const equitySeries = chart.addSeries(LineSeries, {
      color: COLOR_EQUITY,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: true,
      title: 'S&P 500',
    });
    const bondSeries = chart.addSeries(LineSeries, {
      color: COLOR_BOND,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'TLT',
    });
    const baselineSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const corrSeries = {};
    for (const win of CORR_WINDOWS) {
      corrSeries[win.days] = chart.addSeries(LineSeries, {
        color: win.color,
        lineWidth: 1.5,
        lineStyle: 1,
        priceScaleId: 'right',
        priceLineVisible: false,
        lastValueVisible: false,
        title: win.label,
      });
    }
    const zeroSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'right',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const onCrosshairMove = (param) => {
      if (!param.time || param.point == null) {
        setHoverTip(null);
        return;
      }
      const time = crosshairTimeString(param.time);
      const payload = chartPayloadRef.current;
      if (!payload) {
        setHoverTip(null);
        return;
      }
      const situation = situationAtTime(payload.corr30, payload.corr60, payload.corr90, time);
      if (!situation) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: param.point.x,
        y: param.point.y,
        situation,
      });
    };

    chart.subscribeCrosshairMove(onCrosshairMove);
    chartRef.current = chart;
    seriesRef.current = {
      equity: equitySeries,
      bond: bondSeries,
      baseline: baselineSeries,
      corr30: corrSeries[30],
      corr60: corrSeries[60],
      corr90: corrSeries[90],
      zero: zeroSeries,
    };

    const { indexed, corr30, corr60, corr90 } = chartPayload;
    equitySeries.setData(indexed.equity);
    bondSeries.setData(indexed.bond);
    baselineSeries.setData(indexed.equity.map((point) => ({ time: point.time, value: 100 })));
    corrSeries[30].setData(corr30);
    corrSeries[60].setData(corr60);
    corrSeries[90].setData(corr90);
    if (corr90.length) {
      zeroSeries.setData(corr90.map((point) => ({ time: point.time, value: 0 })));
    }

    chart.priceScale('left').applyOptions({
      scaleMargins: { top: 0.2, bottom: 0.14 },
    });
    lockCorrelationScale(chart);
    applyRange(chart, indexed, rangeRef.current);

    const disconnect = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height });
    });

    return () => {
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      disconnect();
      chart.remove();
      chartRef.current = null;
      setHoverTip(null);
      seriesRef.current = {
        equity: null,
        bond: null,
        baseline: null,
        corr30: null,
        corr60: null,
        corr90: null,
        zero: null,
      };
    };
  }, [chartPayload]);

  useEffect(() => {
    if (!chartPayload) return undefined;
    const onWheel = (event) => event.preventDefault();
    const el = chartContainerRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [chartPayload]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [equityBars, bondBars, yieldLatest] = await Promise.all([
          fetchBars(EQUITY_SYMBOL),
          fetchBars(BOND_SYMBOL),
          fetchMacroLatest(YIELD_SERIES),
        ]);
        if (cancelled) return;
        setLatestYield(yieldLatest);

        const aligned = alignCloses(equityBars, bondBars);
        if (!aligned.length) {
          throw new Error('No overlapping dates for ^GSPC and TLT');
        }

        const indexed = toIndexedSeries(aligned);
        const returns = dailyReturns(aligned);
        const corr30 = rollingCorrelation(returns, 30);
        const corr60 = rollingCorrelation(returns, 60);
        const corr90 = rollingCorrelation(returns, 90);

        setSeriesData(indexed);
        setChartPayload({ indexed, corr30, corr60, corr90 });

        const lastEquity = indexed.equity[indexed.equity.length - 1]?.value ?? null;
        const lastBond = indexed.bond[indexed.bond.length - 1]?.value ?? null;
        setLatest({ equity: lastEquity, bond: lastBond });
        setLatestCorr({
          d30: corr30[corr30.length - 1]?.value ?? null,
          d60: corr60[corr60.length - 1]?.value ?? null,
          d90: corr90[corr90.length - 1]?.value ?? null,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load stock/bond data');
          setSeriesData({ equity: [], bond: [], startDate: null });
          setLatest({ equity: null, bond: null });
          setLatestYield(null);
          setLatestCorr({ d30: null, d60: null, d90: null });
          setChartPayload(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRangeChange = useCallback(
    (rangeLabel) => {
      rangeRef.current = rangeLabel;
      setRange(rangeLabel);
      applyRange(chartRef.current, seriesData, rangeLabel);
      lockCorrelationScale(chartRef.current);
    },
    [seriesData],
  );

  return (
    <section className={`stock-bond-panel${fullPage ? ' stock-bond-panel--full' : ''}`}>
      <div className="stock-bond-header">
        <div className="stock-bond-header-main">
          <h3 className="stock-bond-title">Stock vs Bond — Indexed Returns &amp; Correlation</h3>
          <p className="stock-bond-subtitle">
            ^GSPC vs TLT rebased to 100 (TLT since 2002). Left axis: indexed level; right axis:
            rolling correlation (−1 to +1). Hover the chart for the hedge read at that date.
            {latestYield?.value != null && (
              <> 10Y Treasury (FRED) in the legend for rate context.</>
            )}
          </p>
          <StockBondCorrelationHelp />
        </div>
        <div className="stock-bond-legend">
          <span className="stock-bond-legend-item equity">
            ^GSPC {fmtIndexed(latest.equity)}
          </span>
          <span className="stock-bond-legend-item bond">
            TLT {fmtIndexed(latest.bond)}
          </span>
          {latestYield?.value != null && (
            <span className="stock-bond-legend-item yield">
              10Y {Number(latestYield.value).toFixed(2)}%
            </span>
          )}
          <span className={`stock-bond-regime stock-bond-regime--${currentSituation.primaryCss}`}>
            {currentSituation.r90.emoji} {currentSituation.r90.label} (90d)
          </span>
        </div>
      </div>

      {latestCorr.d30 != null && (
        <div className="stock-bond-signal-block">
          <span className={`stock-bond-signal-headline stock-bond-signal-headline--${currentSituation.primaryCss}`}>
            {currentSituation.headline}
          </span>
          <p className="stock-bond-signal-detail">{currentSituation.detail}</p>
          <p className="stock-bond-signal-tooltip-line" title={currentSituation.tooltipLine}>
            {currentSituation.tooltipLine}
          </p>
        </div>
      )}

      <div className="stock-bond-toolbar">
        <span className="stock-bond-range-label">Range</span>
        {RANGE_OPTIONS.map(({ label }) => (
          <button
            key={label}
            type="button"
            className={`stock-bond-range-btn${range === label ? ' active' : ''}`}
            onClick={() => handleRangeChange(label)}
            disabled={loading || seriesData.equity.length === 0}
          >
            {label}
          </button>
        ))}
        {seriesData.startDate && (
          <span className="stock-bond-start">Base: {seriesData.startDate}</span>
        )}
      </div>

      {error && (
        <div className="stock-bond-error">
          {error}. Run investing_backend with price backfill for ^GSPC and TLT.
        </div>
      )}

      {loading && (
        <div className="stock-bond-loading">Loading indexed returns &amp; correlation…</div>
      )}

      {!error && chartPayload && (
        <div className="stock-bond-charts">
          <div className="stock-bond-corr-header">
            <span className="stock-bond-corr-title">Rolling correlation (daily returns)</span>
            <div className="stock-bond-corr-legend">
              {CORR_WINDOWS.map((win) => (
                <span key={win.label} className="stock-bond-corr-item" style={{ color: win.color }}>
                  {win.label}{' '}
                  {fmtCorr(latestCorr[`d${win.days}`])}
                </span>
              ))}
            </div>
          </div>
          <div className="stock-bond-chart-wrap">
            <span className="stock-bond-axis-label stock-bond-axis-label--left">Indexed (100 = start)</span>
            <div
              ref={chartContainerRef}
              className="stock-bond-chart stock-bond-chart-combined"
            />
            {hoverTip && (
              <div
                className="stock-bond-chart-tooltip"
                style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
                role="tooltip"
              >
                <div className="stock-bond-chart-tooltip-date">
                  {formatChartDate(hoverTip.situation.time)}
                </div>
                <div className="stock-bond-chart-tooltip-corr">
                  30d {fmtSignedCorr(hoverTip.situation.c30)} · 60d {fmtSignedCorr(hoverTip.situation.c60)} · 90d{' '}
                  {fmtSignedCorr(hoverTip.situation.c90)}
                </div>
                <div className={`stock-bond-chart-tooltip-regime stock-bond-regime--${hoverTip.situation.primaryCss}`}>
                  {hoverTip.situation.r90.emoji} {hoverTip.situation.headline}
                </div>
                <p className="stock-bond-chart-tooltip-body">{hoverTip.situation.detail}</p>
              </div>
            )}
            <span className="stock-bond-axis-label stock-bond-axis-label--right">Correlation</span>
          </div>
        </div>
      )}
    </section>
  );
}

export default StockBondReturnsPanel;
